import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Checkbox } from '../../ui/checkbox';
import { Textarea } from '../../ui/textarea';
import { 
  Clock, 
  Calendar, 
  Download, 
  Filter,
  AlertTriangle,
  CheckCircle,
  XCircle,
  User as UserIcon,
  Users,
  Timer,
  RefreshCw,
  CheckSquare,
  Check,
  X
} from 'lucide-react';
import { WorkEntry, User } from '../../../types';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { calculateWorkDuration, formatDuration } from '../../../utils/timeUtils';
import { apiService } from '../../../services/api';
import { OvertimeApprovalDialog } from '../OvertimeApprovalDialog';
import { ExtractOvertimeDialog } from '../ExtractOvertimeDialog';
import { useToast } from '../../ui/use-toast';

interface OvertimeTabProps {
  workEntries: WorkEntry[];
  allUsers: User[];
  selectedDate: string;
  onDateChange: (date: string) => void;
  isLoading: boolean;
}

interface OvertimeRecord {
  id?: number;
  userId: number;
  userName: string;
  teamName?: string;
  date: string;
  regularHours: number;
  overtimeHours: number;
  totalHours: number;
  breakMinutes: number;
  workQuota: number;
  totalExpectedHours?: number;
  entries: WorkEntry[];
  isApproved?: boolean;
  isDenied?: boolean;
  approvedByName?: string;
  approvalComment?: string;
  approvedAt?: string;
  reason?: string;
}

export const OvertimeTab: React.FC<OvertimeTabProps> = ({
  workEntries,
  allUsers,
  selectedDate,
  onDateChange,
  isLoading
}) => {
  const [filterTeam, setFilterTeam] = useState<string>('all');
  const [filterUser, setFilterUser] = useState<string>('all');
  const [dateRange, setDateRange] = useState<'day' | 'week' | 'month'>('day');
  const [userWorkSettings, setUserWorkSettings] = useState<Map<number, { breakMinutes: number; workQuota: number }>>(new Map());
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [selectedOvertimeRecord, setSelectedOvertimeRecord] = useState<any>(null);
  const [pendingApprovals, setPendingApprovals] = useState<any[]>([]);
  const [isLoadingApprovals, setIsLoadingApprovals] = useState(false);
  const [showExtractDialog, setShowExtractDialog] = useState(false);
  const [overtimeRecords, setOvertimeRecords] = useState<any[]>([]);
  const [selectedRecords, setSelectedRecords] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkComment, setBulkComment] = useState('');
  const [bulkOvertimeReason, setBulkOvertimeReason] = useState('');
  const [bulkApprovalDetails, setBulkApprovalDetails] = useState('');
  const { toast } = useToast();

  // Load user work settings with proper defaults
  useEffect(() => {
    const loadWorkSettings = async () => {
      const settings = new Map();
      for (const user of allUsers) {
        try {
          const userSettings = await apiService.getUserWorkSettings(user.id);
          // Use actual settings if available, otherwise use defaults (8h work, 1h break)
          settings.set(user.id, {
            breakMinutes: userSettings.dailyBreakMinutes > 0 ? userSettings.dailyBreakMinutes : 60,
            workQuota: userSettings.dailyWorkQuotaMinutes > 0 ? userSettings.dailyWorkQuotaMinutes / 60 : 8
          });
        } catch {
          // Default settings: 8 hours work, 1 hour break
          settings.set(user.id, {
            breakMinutes: 60,
            workQuota: 8
          });
        }
      }
      setUserWorkSettings(settings);
    };
    
    if (allUsers.length > 0) {
      loadWorkSettings();
    }
  }, [allUsers]);

  // Load pending approvals and all overtime records
  useEffect(() => {
    loadPendingApprovals();
    loadOvertimeRecords();
  }, [selectedDate]);

  const loadPendingApprovals = async () => {
    setIsLoadingApprovals(true);
    try {
      const result = await apiService.getPendingOvertimeApprovals();
      setPendingApprovals(result.overtimes);
    } catch (error) {
      console.error('Error loading pending approvals:', error);
    } finally {
      setIsLoadingApprovals(false);
    }
  };
  
  const loadOvertimeRecords = async () => {
    try {
      const result = await apiService.getOvertimes(selectedDate);
      setOvertimeRecords(result.overtimes || []);
    } catch (error) {
      console.error('Error loading overtime records:', error);
    }
  };

  // Calculate overtime for each user
  const overtimeData = useMemo(() => {
    const records: OvertimeRecord[] = [];
    
    // Group entries by user and date
    const userDayEntries = new Map<string, WorkEntry[]>();
    
    workEntries.forEach(entry => {
      if (!entry.date) return;
      
      const dateStr = typeof entry.date === 'string' 
        ? entry.date.split('T')[0]
        : new Date(entry.date).toISOString().split('T')[0];
      
      // Filter by selected date or date range
      if (dateRange === 'day' && dateStr !== selectedDate) return;
      // TODO: Add week and month filtering
      
      const key = `${entry.userId}_${dateStr}`;
      if (!userDayEntries.has(key)) {
        userDayEntries.set(key, []);
      }
      userDayEntries.get(key)!.push(entry);
    });
    
    // Calculate overtime for each user-day combination
    userDayEntries.forEach((entries, key) => {
      const [userIdStr, dateStr] = key.split('_');
      const userId = parseInt(userIdStr);
      const user = allUsers.find(u => u.id === userId);
      
      if (!user) return;
      
      // Get user's work settings (default 8h work, 1h break if not configured)
      const settings = userWorkSettings.get(userId) || { breakMinutes: 60, workQuota: 8 };
      
      // Calculate total worked hours for the day
      let totalMinutes = 0;
      let totalBreakMinutes = 0;
      
      entries.forEach(entry => {
        if (entry.startTime) {
          const duration = calculateWorkDuration(
            entry.startTime, 
            entry.endTime, 
            0 // Don't subtract breaks here, we'll handle them separately
          );
          totalMinutes += duration * 60;
          totalBreakMinutes += entry.breakMinutes || entry.totalBreakMinutes || 0;
        }
      });
      
      // Calculate net worked hours (actual work time, excluding breaks)
      const netMinutes = totalMinutes - totalBreakMinutes;
      const totalHours = netMinutes / 60;
      
      // Calculate total expected time (work quota + break quota)
      const totalExpectedHours = settings.workQuota + (settings.breakMinutes / 60);
      
      // Calculate total time at work (including breaks)
      const totalTimeAtWorkHours = totalMinutes / 60;
      
      // Overtime starts after total expected time (e.g., after 9h for 8h work + 1h break)
      const overtimeHours = Math.max(0, totalTimeAtWorkHours - totalExpectedHours);
      
      // Regular hours is the worked time up to the work quota
      const regularHours = Math.min(totalHours, settings.workQuota);
      
      // Only add if there's actual work recorded
      if (totalHours > 0) {
        // Find matching overtime record from API
        const overtimeRecord = overtimeRecords.find(
          or => or.userId === userId && or.date === dateStr
        );
        
        records.push({
          id: overtimeRecord?.id,
          userId,
          userName: user.name,
          teamName: user.team?.name,
          date: dateStr,
          regularHours,
          overtimeHours,
          totalHours,
          breakMinutes: totalBreakMinutes,
          workQuota: settings.workQuota,
          totalExpectedHours: totalExpectedHours, // Add this for display
          entries,
          isApproved: overtimeRecord?.isApproved || false,
          isDenied: overtimeRecord?.isDenied || false,
          approvedByName: overtimeRecord?.approvedByName,
          approvalComment: overtimeRecord?.approvalComment,
          approvedAt: overtimeRecord?.approvedAt,
          reason: overtimeRecord?.reason
        });
      }
    });
    
    // Sort by overtime hours (descending)
    records.sort((a, b) => b.overtimeHours - a.overtimeHours);
    
    return records;
  }, [workEntries, allUsers, selectedDate, dateRange, userWorkSettings, overtimeRecords]);

  // Filter data based on team and user selection
  const filteredData = useMemo(() => {
    let filtered = overtimeData;
    
    if (filterTeam !== 'all') {
      filtered = filtered.filter(r => r.teamName === filterTeam);
    }
    
    if (filterUser !== 'all') {
      filtered = filtered.filter(r => r.userId === parseInt(filterUser));
    }
    
    return filtered;
  }, [overtimeData, filterTeam, filterUser]);


  // Get unique teams for filter
  const teams = useMemo(() => {
    const uniqueTeams = new Set<string>();
    allUsers.forEach(u => {
      if (u.team?.name) uniqueTeams.add(u.team.name);
    });
    return Array.from(uniqueTeams);
  }, [allUsers]);

  const formatHoursMinutes = (decimalHours: number): string => {
    const totalMinutes = Math.round(decimalHours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}h ${m}m`;
  };

  const exportToCSV = () => {
    const headers = ['Nume', 'Echipa', 'Data', 'Ore Normale', 'Ore Suplimentare', 'Total Ore', 'Pauza (min)', 'Norma Zilnica', 'Status', 'Aprobat/Respins De', 'Data Aprobare', 'Motiv Overtime', 'Detalii Decizie'];
    const rows = filteredData.map(r => {
      // Parse the approval comment to extract reason and details
      let overtimeReason = '-';
      let approvalDetails = '-';

      if (r.approvalComment) {
        if (r.approvalComment.includes('MOTIV OVERTIME:')) {
          const parts = r.approvalComment.split('\n\nDETALII APROBARE:');
          overtimeReason = parts[0]?.replace('MOTIV OVERTIME:', '').trim() || '-';
          approvalDetails = parts[1]?.trim() || r.approvalComment;
        } else if (r.approvalComment.includes('MOTIV RESPINGERE:')) {
          approvalDetails = r.approvalComment.replace('MOTIV RESPINGERE:', '').trim();
        } else {
          approvalDetails = r.approvalComment;
        }
      }

      // Use existing reason if no approval comment
      if (overtimeReason === '-' && r.reason) {
        overtimeReason = r.reason;
      }

      return [
        r.userName,
        r.teamName || '-',
        r.date,
        formatHoursMinutes(r.regularHours),
        formatHoursMinutes(r.overtimeHours),
        formatHoursMinutes(r.totalHours),
        r.breakMinutes.toString(),
        r.workQuota.toString(),
        r.isApproved ? 'Aprobat' : r.isDenied ? 'Respins' : 'In Asteptare',
        r.approvedByName || '-',
        r.approvedAt ? format(new Date(r.approvedAt), 'dd.MM.yyyy HH:mm') : '-',
        overtimeReason,
        approvalDetails
      ];
    });

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `overtime_${selectedDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleApprovalClick = (record: any) => {
    setSelectedOvertimeRecord({
      id: record.id || Math.random(), // Temporary ID for frontend records
      userId: record.userId,
      userName: record.userName,
      date: record.date,
      regularHours: record.regularHours,
      overtimeHours: record.overtimeHours,
      totalHours: record.totalHours,
      breakMinutes: record.breakMinutes,
      reason: record.reason || 'Daily overtime',
    });
    setShowApprovalDialog(true);
  };

  const handleCalculateDailyOvertime = async () => {
    try {
      const result = await apiService.calculateDailyOvertime(new Date(selectedDate));
      toast({
        title: 'Calcul ore suplimentare',
        description: `S-au procesat ${result.recordsProcessed} înregistrări pentru ${result.date}`,
      });
      // Refresh the data
      loadPendingApprovals();
    } catch (error) {
      console.error('Error calculating daily overtime:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut calcula orele suplimentare',
        variant: 'destructive',
      });
    }
  };

  // Handle bulk selection
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newSelected = new Set<string>();
      filteredData.forEach(record => {
        if (record.overtimeHours > 0 && !record.isApproved && !record.isDenied) {
          newSelected.add(`${record.userId}_${record.date}`);
        }
      });
      setSelectedRecords(newSelected);
    } else {
      setSelectedRecords(new Set());
    }
  };

  const handleSelectRecord = (recordId: string, checked: boolean) => {
    const newSelected = new Set(selectedRecords);
    if (checked) {
      newSelected.add(recordId);
    } else {
      newSelected.delete(recordId);
    }
    setSelectedRecords(newSelected);
  };

  // Handle bulk approve
  const handleBulkApprove = async () => {
    if (!bulkOvertimeReason || !bulkApprovalDetails) {
      toast({
        title: 'Date incomplete',
        description: 'Vă rugăm să completați motivul overtime și detaliile aprobării',
        variant: 'destructive',
      });
      return;
    }

    try {
      const selectedData = filteredData.filter(record => 
        selectedRecords.has(`${record.userId}_${record.date}`)
      );

      // First, calculate overtime for records without IDs
      const recordsWithoutId = selectedData.filter(r => !r.id);
      if (recordsWithoutId.length > 0) {
        // Calculate daily overtime to create records in backend
        const uniqueDates = [...new Set(recordsWithoutId.map(r => r.date))];
        for (const date of uniqueDates) {
          try {
            await apiService.calculateDailyOvertime(new Date(date));
          } catch (error) {
            console.error(`Error calculating overtime for ${date}:`, error);
          }
        }
        
        // Reload overtime records to get the new IDs
        await loadOvertimeRecords();
        
        // Re-filter to get updated records with IDs
        const updatedData = filteredData.filter(record => 
          selectedRecords.has(`${record.userId}_${record.date}`)
        );
        
        for (const record of updatedData) {
          if (record.id) {
            await apiService.approveOvertime(record.id, {
              isApproved: true,
              approvedById: currentAdmin?.id || 1,
              comment: `MOTIV OVERTIME: ${bulkOvertimeReason}\n\nDETALII APROBARE: ${bulkApprovalDetails}`,
            });
          }
        }
      } else {
        // All records have IDs, proceed normally
        for (const record of selectedData) {
          if (record.id) {
            await apiService.approveOvertime(record.id, {
              isApproved: true,
              approvedById: currentAdmin?.id || 1,
              comment: `MOTIV OVERTIME: ${bulkOvertimeReason}\n\nDETALII APROBARE: ${bulkApprovalDetails}`,
            });
          }
        }
      }

      // Update local state immediately to reflect changes
      const approvedRecordIds = selectedData.map(r => `${r.userId}_${r.date}`);
      
      // Update pending approvals - remove approved items
      setPendingApprovals(prev => 
        prev.filter(p => !approvedRecordIds.includes(`${p.userId}_${p.date}`))
      );
      
      // Update overtime records with approval status
      setOvertimeRecords(prev => {
        const updated = [...prev];
        selectedData.forEach(record => {
          const idx = updated.findIndex(r => 
            r.userId === record.userId && r.date === record.date
          );
          if (idx !== -1) {
            updated[idx] = {
              ...updated[idx],
              isApproved: true,
              isDenied: false,
              approvedByName: currentAdmin?.name || 'Admin',
              approvalComment: `MOTIV OVERTIME: ${bulkOvertimeReason}\n\nDETALII APROBARE: ${bulkApprovalDetails}`,
              approvedAt: new Date().toISOString()
            };
          }
        });
        return updated;
      });

      toast({
        title: 'Succes',
        description: `${selectedData.length} ore suplimentare au fost aprobate`,
      });

      // Clear selections
      setSelectedRecords(new Set());
      setShowBulkActions(false);
      setBulkOvertimeReason('');
      setBulkApprovalDetails('');
    } catch (error) {
      console.error('Error in bulk approve:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut aproba orele suplimentare',
        variant: 'destructive',
      });
    }
  };

  // Handle bulk reject
  const handleBulkReject = async () => {
    if (!bulkComment) {
      toast({
        title: 'Date incomplete',
        description: 'Vă rugăm să specificați motivul respingerii',
        variant: 'destructive',
      });
      return;
    }

    try {
      const selectedData = filteredData.filter(record => 
        selectedRecords.has(`${record.userId}_${record.date}`)
      );

      // First, calculate overtime for records without IDs
      const recordsWithoutId = selectedData.filter(r => !r.id);
      if (recordsWithoutId.length > 0) {
        // Calculate daily overtime to create records in backend
        const uniqueDates = [...new Set(recordsWithoutId.map(r => r.date))];
        for (const date of uniqueDates) {
          try {
            await apiService.calculateDailyOvertime(new Date(date));
          } catch (error) {
            console.error(`Error calculating overtime for ${date}:`, error);
          }
        }
        
        // Reload overtime records to get the new IDs
        await loadOvertimeRecords();
        
        // Re-filter to get updated records with IDs
        const updatedData = filteredData.filter(record => 
          selectedRecords.has(`${record.userId}_${record.date}`)
        );
        
        for (const record of updatedData) {
          if (record.id) {
            await apiService.approveOvertime(record.id, {
              isApproved: false,
              approvedById: currentAdmin?.id || 1,
              comment: `MOTIV RESPINGERE: ${bulkComment}`,
            });
          }
        }
      } else {
        // All records have IDs, proceed normally
        for (const record of selectedData) {
          if (record.id) {
            await apiService.approveOvertime(record.id, {
              isApproved: false,
              approvedById: currentAdmin?.id || 1,
              comment: `MOTIV RESPINGERE: ${bulkComment}`,
            });
          }
        }
      }

      // Update local state immediately to reflect changes
      const rejectedRecordIds = selectedData.map(r => `${r.userId}_${r.date}`);
      
      // Update pending approvals - remove rejected items
      setPendingApprovals(prev => 
        prev.filter(p => !rejectedRecordIds.includes(`${p.userId}_${p.date}`))
      );
      
      // Update overtime records with rejection status
      setOvertimeRecords(prev => {
        const updated = [...prev];
        selectedData.forEach(record => {
          const idx = updated.findIndex(r => 
            r.userId === record.userId && r.date === record.date
          );
          if (idx !== -1) {
            updated[idx] = {
              ...updated[idx],
              isApproved: false,
              isDenied: true,
              approvedByName: currentAdmin?.name || 'Admin',
              approvalComment: `MOTIV RESPINGERE: ${bulkComment}`,
              approvedAt: new Date().toISOString()
            };
          }
        });
        return updated;
      });

      toast({
        title: 'Succes',
        description: `${selectedData.length} ore suplimentare au fost respinse`,
      });

      // Clear selections
      setSelectedRecords(new Set());
      setShowBulkActions(false);
      setBulkComment('');
    } catch (error) {
      console.error('Error in bulk reject:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut respinge orele suplimentare',
        variant: 'destructive',
      });
    }
  };

  // Get approvers (admins and supervisors)
  const approvers = allUsers.filter(u => u.role === 'admin' || u.role === 'supervisor');
  const currentAdmin = allUsers.find(u => u.role === 'admin') || allUsers[0];

  return (
    <div className="space-y-6">
      {/* Pending Approvals Alert */}
      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filtre și Perioadă
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowExtractDialog(true)}
              className="flex items-center gap-2"
            >
              <Calendar className="h-4 w-4" />
              Extrage din Zile Anterioare
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Data</Label>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => onDateChange(e.target.value)}
              />
            </div>
            
            <div>
              <Label>Perioadă</Label>
              <Select value={dateRange} onValueChange={(v: any) => setDateRange(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Zi</SelectItem>
                  <SelectItem value="week">Săptămână</SelectItem>
                  <SelectItem value="month">Lună</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Echipă</Label>
              <Select value={filterTeam} onValueChange={setFilterTeam}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate echipele</SelectItem>
                  {teams.map(team => (
                    <SelectItem key={team} value={team}>{team}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Muncitor</Label>
              <Select value={filterUser} onValueChange={setFilterUser}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toți muncitorii</SelectItem>
                  {allUsers
                    .filter(u => u.role === 'worker')
                    .map(user => (
                      <SelectItem key={user.id} value={user.id.toString()}>
                        {user.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>


      {/* Bulk Actions Card */}
      {selectedRecords.size > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <CheckSquare className="h-5 w-5" />
                {selectedRecords.size} înregistrări selectate
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelectedRecords(new Set());
                  setShowBulkActions(false);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!showBulkActions ? (
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowBulkActions(true)}
                  className="flex items-center gap-2"
                  variant="default"
                >
                  <Check className="h-4 w-4" />
                  Aprobă Selectate
                </Button>
                <Button
                  onClick={() => setShowBulkActions(true)}
                  className="flex items-center gap-2"
                  variant="destructive"
                >
                  <X className="h-4 w-4" />
                  Respinge Selectate
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-4 p-4 bg-green-50 rounded-lg">
                    <h4 className="font-semibold text-green-800">Aprobare în Masă</h4>
                    <div>
                      <Label>Motiv Ore Suplimentare *</Label>
                      <Textarea
                        value={bulkOvertimeReason}
                        onChange={(e) => setBulkOvertimeReason(e.target.value)}
                        placeholder="Ex: Termen limită proiect urgent, volum mare de lucru..."
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Detalii Aprobare *</Label>
                      <Textarea
                        value={bulkApprovalDetails}
                        onChange={(e) => setBulkApprovalDetails(e.target.value)}
                        placeholder="Ex: Aprobat pentru finalizarea lucrărilor la termenul stabilit..."
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                    <Button
                      onClick={handleBulkApprove}
                      className="w-full"
                      variant="default"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Aprobă {selectedRecords.size} Înregistrări
                    </Button>
                  </div>
                  
                  <div className="space-y-4 p-4 bg-red-50 rounded-lg">
                    <h4 className="font-semibold text-red-800">Respingere în Masă</h4>
                    <div>
                      <Label>Motiv Respingere *</Label>
                      <Textarea
                        value={bulkComment}
                        onChange={(e) => setBulkComment(e.target.value)}
                        placeholder="Ex: Ore neautorizate, lipsă justificare..."
                        className="mt-1"
                        rows={6}
                      />
                    </div>
                    <Button
                      onClick={handleBulkReject}
                      className="w-full"
                      variant="destructive"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Respinge {selectedRecords.size} Înregistrări
                    </Button>
                  </div>
                </div>
                <Button
                  onClick={() => {
                    setShowBulkActions(false);
                    setBulkOvertimeReason('');
                    setBulkApprovalDetails('');
                    setBulkComment('');
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Anulează
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Detailed Overtime Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Timer className="h-5 w-5" />
              Detalii Ore Suplimentare
            </CardTitle>
            <Button onClick={exportToCSV} size="sm" variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {filteredData.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Timer className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p className="text-lg font-medium">Nu există date de overtime pentru perioada selectată</p>
              <p className="text-sm mt-2">Verificați filtrele sau selectați o altă perioadă</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-center p-2 w-12">
                      <Checkbox
                        checked={
                          filteredData.filter(r => r.overtimeHours > 0 && !r.isApproved && !r.isDenied).length > 0 &&
                          filteredData.filter(r => r.overtimeHours > 0 && !r.isApproved && !r.isDenied).every(r => 
                            selectedRecords.has(`${r.userId}_${r.date}`)
                          )
                        }
                        onCheckedChange={handleSelectAll}
                      />
                    </th>
                    <th className="text-left p-2">Muncitor</th>
                    <th className="text-left p-2">Echipă</th>
                    <th className="text-left p-2">Data</th>
                    <th className="text-center p-2">Ore Lucrate</th>
                    <th className="text-center p-2">Ore Suplimentare</th>
                    <th className="text-center p-2">Status</th>
                    <th className="text-left p-2">Motiv Overtime</th>
                    <th className="text-center p-2">Aprobat De</th>
                    <th className="text-center p-2">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((record, idx) => {
                    const recordId = `${record.userId}_${record.date}`;
                    const canSelect = record.overtimeHours > 0 && !record.isApproved && !record.isDenied;
                    const needsBackendRecord = canSelect && !record.id;
                    
                    return (
                      <tr key={`${recordId}_${idx}`} className="border-b hover:bg-gray-50">
                        <td className="text-center p-2">
                          {canSelect && (
                            <Checkbox
                              checked={selectedRecords.has(recordId)}
                              onCheckedChange={(checked) => handleSelectRecord(recordId, checked as boolean)}
                              title={needsBackendRecord ? "Înregistrare locală - va fi creată la aprobare" : undefined}
                            />
                          )}
                        </td>
                        <td className="p-2">
                          <div className="flex items-center gap-2">
                            <UserIcon className="h-4 w-4 text-gray-400" />
                            <span className="font-medium">{record.userName}</span>
                          </div>
                        </td>
                      <td className="p-2">
                        <Badge variant="outline">{record.teamName || '-'}</Badge>
                      </td>
                      <td className="p-2">
                        {format(new Date(record.date + 'T00:00:00'), 'dd MMM', { locale: ro })}
                      </td>
                      <td className="text-center p-2">
                        <span className="font-medium">{formatDuration(record.totalHours)}</span>
                      </td>
                      <td className="text-center p-2">
                        {record.overtimeHours > 0 ? (
                          <Badge variant="destructive">{formatDuration(record.overtimeHours)}</Badge>
                        ) : (
                          <Badge variant="outline">-</Badge>
                        )}
                      </td>
                      <td className="text-center p-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="inline-block">
                                {record.overtimeHours > 0 ? (
                                  record.isApproved ? (
                                    <Badge variant="default" className="flex items-center gap-1 w-fit mx-auto cursor-help">
                                      <CheckCircle className="h-3 w-3" />
                                      Aprobat
                                    </Badge>
                                  ) : record.isDenied ? (
                                    <Badge variant="destructive" className="flex items-center gap-1 w-fit mx-auto cursor-help">
                                      <XCircle className="h-3 w-3" />
                                      Respins
                                    </Badge>
                                  ) : (
                                    <Badge variant="warning" className="flex items-center gap-1 w-fit mx-auto">
                                      <AlertTriangle className="h-3 w-3" />
                                      În Așteptare
                                    </Badge>
                                  )
                                ) : record.totalHours >= record.workQuota ? (
                                  <Badge variant="default" className="flex items-center gap-1 w-fit mx-auto">
                                    <CheckCircle className="h-3 w-3" />
                                    Complet
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary" className="flex items-center gap-1 w-fit mx-auto">
                                    <Clock className="h-3 w-3" />
                                    Normal
                                  </Badge>
                                )}
                              </div>
                            </TooltipTrigger>
                            {record.approvalComment && (
                              <TooltipContent className="max-w-sm">
                                <div className="space-y-2">
                                  {(() => {
                                    if (record.approvalComment.includes('MOTIV OVERTIME:')) {
                                      const parts = record.approvalComment.split('\n\nDETALII APROBARE:');
                                      const reason = parts[0]?.replace('MOTIV OVERTIME:', '').trim();
                                      const details = parts[1]?.trim();
                                      return (
                                        <>
                                          <div>
                                            <p className="font-semibold text-xs">Motiv Overtime:</p>
                                            <p className="text-xs">{reason}</p>
                                          </div>
                                          <div>
                                            <p className="font-semibold text-xs">Detalii Aprobare:</p>
                                            <p className="text-xs">{details}</p>
                                          </div>
                                        </>
                                      );
                                    } else if (record.approvalComment.includes('MOTIV RESPINGERE:')) {
                                      const reason = record.approvalComment.replace('MOTIV RESPINGERE:', '').trim();
                                      return (
                                        <div>
                                          <p className="font-semibold text-xs">Motiv Respingere:</p>
                                          <p className="text-xs">{reason}</p>
                                        </div>
                                      );
                                    }
                                    return <p className="text-xs">{record.approvalComment}</p>;
                                  })()}
                                  {record.approvedByName && (
                                    <div className="pt-2 border-t">
                                      <p className="text-xs text-gray-500">
                                        {record.isApproved ? 'Aprobat' : 'Respins'} de: {record.approvedByName}
                                      </p>
                                      {record.approvedAt && (
                                        <p className="text-xs text-gray-500">
                                          La: {format(new Date(record.approvedAt), 'dd.MM.yyyy HH:mm')}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      </td>
                      <td className="text-left p-2 max-w-xs">
                        {(() => {
                          let overtimeReason = '';
                          if (record.approvalComment?.includes('MOTIV OVERTIME:')) {
                            const parts = record.approvalComment.split('\n\nDETALII APROBARE:');
                            overtimeReason = parts[0]?.replace('MOTIV OVERTIME:', '').trim() || '';
                          } else if (record.reason) {
                            overtimeReason = record.reason;
                          }
                          
                          return overtimeReason ? (
                            <span className="text-sm text-gray-700 line-clamp-2" title={overtimeReason}>
                              {overtimeReason}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          );
                        })()}
                      </td>
                      <td className="text-center p-2">
                        {record.approvedByName ? (
                          <div className="text-sm">
                            <p className="font-medium">{record.approvedByName}</p>
                            {record.approvedAt && (
                              <p className="text-xs text-gray-500">
                                {format(new Date(record.approvedAt), 'dd.MM HH:mm')}
                              </p>
                            )}
                            {record.approvalComment && (
                              <div className="mt-1">
                                {(() => {
                                  let details = '';
                                  if (record.approvalComment.includes('DETALII APROBARE:')) {
                                    const parts = record.approvalComment.split('\n\nDETALII APROBARE:');
                                    details = parts[1]?.trim() || '';
                                  } else if (record.approvalComment.includes('MOTIV RESPINGERE:')) {
                                    details = record.approvalComment.replace('MOTIV RESPINGERE:', '').trim();
                                  }
                                  
                                  return details ? (
                                    <span 
                                      className="text-xs text-blue-600 cursor-help underline" 
                                      title={details}
                                    >
                                      Vezi detalii
                                    </span>
                                  ) : null;
                                })()}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="text-center p-2">
                        {record.overtimeHours > 0 && !record.isApproved && !record.isDenied && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApprovalClick(record)}
                          >
                            <CheckSquare className="h-4 w-4 mr-1" />
                            Procesează
                          </Button>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Approval Dialog */}
      <OvertimeApprovalDialog
        isOpen={showApprovalDialog}
        onClose={() => {
          setShowApprovalDialog(false);
          setSelectedOvertimeRecord(null);
        }}
        overtimeRecord={selectedOvertimeRecord}
        approvers={approvers}
        currentUser={currentAdmin}
        onApprovalComplete={(approved: boolean, overtimeId: number) => {
          // Update local state immediately
          if (selectedOvertimeRecord) {
            const recordId = `${selectedOvertimeRecord.userId}_${selectedOvertimeRecord.date}`;
            
            // Update pending approvals - remove processed item
            setPendingApprovals(prev => 
              prev.filter(p => p.id !== overtimeId)
            );
            
            // Update overtime records with new status
            setOvertimeRecords(prev => {
              const updated = [...prev];
              const idx = updated.findIndex(r => r.id === overtimeId);
              if (idx !== -1) {
                updated[idx] = {
                  ...updated[idx],
                  isApproved: approved,
                  isDenied: !approved,
                  approvedByName: currentAdmin?.name || 'Admin',
                  approvedAt: new Date().toISOString()
                };
              }
              return updated;
            });
          }
        }}
      />
      
      {/* Extract Overtime Dialog */}
      <ExtractOvertimeDialog
        isOpen={showExtractDialog}
        onClose={() => setShowExtractDialog(false)}
        onExtractionComplete={() => {
          loadPendingApprovals();
          // Optionally trigger a full data refresh here
          toast({
            title: 'Date actualizate',
            description: 'Orele suplimentare au fost extrase și actualizate',
          });
        }}
      />
    </div>
  );
};